import React, { ForwardRefRenderFunction, forwardRef, useMemo } from "react";
//#region types
import { CurrencyEnums, FormEnums } from "models/enums";
import { AutoCompleteExtraProps, FormHandles, GenericFormSection } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { SupplierFormTypes } from "models/formTypes";
import { SupplierSettingsFormViewProps } from "models/propTypes/supplierPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region  components
import { DateRange, GenericForm, NumberRange, Tags } from "components/common";
import SupplierRuleFormContainer from "./ruleFormContainer";
import { Grid } from "@mui/material";
import { useSupplierService } from "serviceHooks";
//#endregion
import { CountryHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const SupplierSettingsForm: ForwardRefRenderFunction<FormHandles, SupplierSettingsFormViewProps> = (props, ref) => {
  const supplierService = useSupplierService();
  const { onToggleRuleActivation, supplierId } = props;
  const formFields = useMemo(
    (): GenericFormSection<SupplierFormTypes.Settings>[] => [
      {
        label: TranslationKeys.Common.General,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WInfo,
        innerFields: {
          supplierName: {
            label: TranslationKeys.Common.Company,
            type: FormEnums.FormFieldType.TEXT,
            disabled: true,
            descriptionText: TranslationKeys.Common.Description,
          },
          supplierOrgNr: {
            label: TranslationKeys.Common.OrgNr,
            type: FormEnums.FormFieldType.TEXT,
            disabled: true,
            descriptionText: TranslationKeys.Common.Description,
          },
          paymentTerms: {
            label: TranslationKeys.Common.PaymentTerms,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            descriptionText: TranslationKeys.Supplier.PaymentTermsHelpText,
            extraProps: {
              referenceFieldName: FormFieldConstants.Supplier.Settings.PaymentTerms,
              isNumericString: true,
              allowNegative: false,
              freeSolo: true,
              valueSuffix: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Days),
              items: InvoiceConstants.PaymentTerms.map((item) => ({
                id: item,
                value: item.toString(),
                group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
              })),
            } as AutoCompleteExtraProps<SupplierFormTypes.Settings>,
          },
          pg: {
            label: TranslationKeys.Common.PG_BG,
            type: FormEnums.FormFieldType.TEXT,
            disabled: true,
            descriptionText: TranslationKeys.Common.Description,
          },
          address: {
            label: TranslationKeys.Common.Address,
            type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
            descriptionText: TranslationKeys.Common.Description,
            innerFields: {
              street1: {
                label: TranslationKeys.Common.StreetAddress,
                type: FormEnums.FormFieldType.TEXT,
                maxLength: 100,
                widthColumnCount: 12,
              },
              street2: {
                label: TranslationKeys.Common.StreetAddress2,
                type: FormEnums.FormFieldType.TEXT,
                maxLength: 100,
                widthColumnCount: 12,
              },
              zipCode: {
                label: TranslationKeys.Common.PostalCode,
                type: FormEnums.FormFieldType.TEXT,
                maxLength: 6,
              },
              city: {
                label: TranslationKeys.Common.City,
                type: FormEnums.FormFieldType.TEXT,
                maxLength: 100,
              },
              countryName: {
                label: TranslationKeys.Common.Country,
                type: FormEnums.FormFieldType.AUTOCOMPLETE,
                widthColumnCount: 12,
                extraProps: {
                  referenceFieldName: FormFieldConstants.Supplier.Settings.Country,
                  items: CountryHelper.getCountryAutoCompleteList(),
                },
              },
            },
          },
        },
      },
      {
        label: TranslationKeys.Common.Automation,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WBolt,
        innerFields: {
          rules: {
            label: TranslationKeys.Common.AutoApprovals,
            type: FormEnums.FormFieldType.TABLELIST,
            extraProps: {
              entityText: TranslationKeys.Common.AutomaticApproval,
              activeKey: "active",
              resultTableProperties: {
                amount: {
                  formatter: (value: WintSuperkollAPIModel.IncomingInvoiceRule) => (
                    <Grid item container direction="column">
                      <Grid item className={cssExports.tableListMainText}>
                        <NumberRange
                          lowest={value.minAmount}
                          highest={value.maxAmount}
                          currencyCode={CurrencyEnums.CurrencyCode.SEK}
                        />
                      </Grid>
                      {value.bookingAccountName && <Grid item>{`${value.bookingAccountName}`}</Grid>}
                    </Grid>
                  ),
                  noWrap: true,
                },
                rulePeriod: {
                  formatter: (value: WintSuperkollAPIModel.IncomingInvoiceRule) => (
                    <Grid item container direction="column">
                      <Grid item className={cssExports.tableListMainText}>
                        {supplierService.getRulePeriodShortText(value.rulePeriod)}
                      </Grid>
                      <Grid item>
                        <DateRange start={value.fromDate} end={value.toDate} />
                      </Grid>
                      <Grid item>{`${value.dimensions?.map((d) => d.dimensionName).join(", ")}`}</Grid>
                    </Grid>
                  ),
                },
                active: {
                  formatter: (value: WintSuperkollAPIModel.IncomingInvoiceRule) => (
                    <Tags
                      tags={[
                        LocalizationHelperInstance.getFormattedMessage(
                          value.active ? TranslationKeys.Common.Active : TranslationKeys.Common.InActive
                        ),
                      ]}
                      className={value.active ? cssExports.greenTag : cssExports.redTag}
                    />
                  ),
                },
              },
              addItemForm: (values: WintSuperkollAPIModel.IncomingInvoiceRule) => (
                <SupplierRuleFormContainer supplierId={supplierId}></SupplierRuleFormContainer>
              ),
              moreActions: (row: WintSuperkollAPIModel.IncomingInvoiceRule) => [
                {
                  icon: IconDefinitionConstants.WCheck,
                  description: LocalizationHelperInstance.getFormattedMessage(
                    row.active ? TranslationKeys.Common.Deactivate : TranslationKeys.Common.Activate
                  ),
                  onClickHandler: onToggleRuleActivation,
                },
              ],
              addItemDialogSubTitle: LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Supplier.AddRuleDialogSubTitle
              ),
            },
          },
        },
      },
    ],
    []
  );
  return <GenericForm formRef={ref} {...props} fields={formFields} inline />;
};

export default forwardRef(SupplierSettingsForm);
